import { Component, OnInit } from '@angular/core';
import { LogService } from '../../../services/log.service';
import { NgbDropdownConfig } from '@ng-bootstrap/ng-bootstrap';
import { AdminService } from '../../../services/admin.service';
import { AdminUser } from '../../../types/admin/admin-user';
import _ from 'lodash';
import { SpinnerService } from '../../../services/spinner.service';
import { AdminFacility } from '../../../types/admin/admin-facility';
import { SessionService } from '../../../services/session.service';
import { ModalService } from '../../../services/modal.service';
import { BulkLoadDialogComponent } from '../bulk-load-dialog/bulk-load-dialog.component';
import { AccessControlService } from '../../../services/access-control.service';

@Component({
  selector: 'app-admin-user-list',
  templateUrl: './user-list.component.html',
  styleUrls: ['./user-list.component.scss']
})
export class UserListComponent implements OnInit {
  users: AdminUser[];
  searchText = '';
  showInactive = true;
  tooltips = [
    'Select an organization to upload users',
    'Select an organization to add new users',
    'Save or cancel user detail changes before selecting another organization'
  ];
  bulkUploadTooltip = this.tooltips[0];
  addUserTooltip = this.tooltips[1];
  orgListTooltip: string;
  selectedOrganizationUserPoolId: string="";
  emptyMessage="Select an organization to view its users.";

  constructor(
    private log: LogService,
    config: NgbDropdownConfig,
    public admin: AdminService,
    private spinner: SpinnerService,
    public session: SessionService,
    private modal: ModalService,
    private access: AccessControlService
  ) {
    config.placement = 'bottom-left';
    config.autoClose = true;

    admin.saveUserEvent.subscribe(() => {
      this.filterUsers();
    });
  }

  ngOnInit() {
    this.filterUsers();

    if (this.admin.isDevMode()) {
      this.loadUsers();
    }
  }

  isOrgSelected(): boolean {
    return this.admin.selectedOrg != null;
  }

  selectOrganization(userPoolId: string) {
    if (this.admin.getSelectedUser()) {
      this.admin.setSelectedUser(null);
      this.selectOrganization(userPoolId);
    } else {
      this.log.trace('UserList.selectOrganization', { userPoolId: userPoolId });
      if (!userPoolId) {
        this.reset();
      } else {
        this.spinner.showSpinner(
          'Loading organization details, please wait...'
        );
        const callback = () => {
          this.filterUsers();
          this.bulkUploadTooltip = '';
          this.addUserTooltip = '';
          this.spinner.hideSpinner();
        };
        this.admin.loadOrganizationDetails(userPoolId, callback);
      }
    }
  }

  getFacilities(): AdminFacility[] {
    if (this.admin.selectedOrg) {
      return this.admin.selectedOrg.Facilities;
    } else {
      return [];
    }
  }

  selectFacility(facilityCode: string) {
    this.admin.selectFacility(facilityCode);
    this.filterUsers();
  }

  searchUsers() {
    this.log.trace('admin.searchUsers', this.searchText);
    this.filterUsers();
  }

  onShowInactiveChange() {
    this.log.trace('show inactive', this.showInactive);
    this.filterUsers();
  }

  selectUser(user: AdminUser) {
    this.admin.setSelectedUser(user);
  }

  loadUsers() {
    this.modal.open(
      BulkLoadDialogComponent,
      { size: 'lg' },
      () => {
        this.log.trace('load users');
      },
      () => {
        this.log.trace('do nothing');
      }
    );
  }

  canBulkLoad() {
    const user = this.session.getUser();
    return user.isMxAdmin || user.isOrgAdmin || user.isFacAdmin;
  }

  canAddNewUser() {
    const user = this.session.getUser();
    return user.isMxAdmin || user.isOrgAdmin || user.isFacAdmin;
  }

  // Call this method to programmatically set the dropdown value
  setOrganization(value: string) {
    this.selectedOrganizationUserPoolId = value;
    this.selectOrganization(value);
  }

  private reset() {
    this.admin.reset();
    this.users = [];
    this.bulkUploadTooltip = 'Select an organization to upload users';
    this.addUserTooltip = 'Select an organization to add new users';
  }

  private filterUsers() {
    const filter = (user: AdminUser) => {
      // show only active users if showInactive if OFF
      if (!user.IsActive && !this.showInactive) {
        return false;
      }

      // show only users with selected facility
      if (this.admin.selectedFac) {
        if (!user.hasFacility(this.admin.selectedFac.FacilityCode)) {
          // this.log.trace('false', {
          //   user: user,
          //   facility: this.admin.selectedFac
          // });
          return false;
        }
      }

      // match user against search text
      return user.isMatch(this.searchText);
    };

    this.users = _.filter(this.admin.users, filter);
    this.emptyMessage="Select an organization to view its users.";
    
    //For MX Admin users, if no match is found and search field is an email,
    //  search HS across all organizations
    if (this.users.length==0) {
      const emailRegex = /^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/;
      if (emailRegex.test(this.searchText)) {
        const user = this.session.getUser();
        if (user.isMxAdmin) {
          console.log("searching for user "+this.searchText+" in hs");
          const callback = (data) => {
            if (data && data.OrganizationPoolId) {
              this.emptyMessage="Select an organization to view its users.";
              this.setOrganization(data.OrganizationPoolId);
            }
            else {
              this.emptyMessage="No user found across all organizations."
            }
          };
          this.admin.searchUser(this.searchText, callback);
        }
      }
    }



    // select first user automatically
    // if (this.users.length > 0) {
    //   this.admin.setSelectedUser(this.users[0]);
    // }
  }
}
